import { useContext, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Button, TextField, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'

import { AppContext } from '/providers/app-context'
import { duplicateTemplate } from '/providers/templates-api'
import PageWrapper from '/components/page-wrapper'
import Menu from '/components/templates/template-nav-menu'
import HelpText from '/help/template-duplicator'
import { setCurrentTemplateId } from '/utils/current-template'

const TemplateDuplicator = () => {
  const { templateId } = useParams()
  const navigate = useNavigate()
  const { apiSource, apiKey, loadingTemplates, templates, refreshTemplates, processing, setProcessing } = useContext(AppContext)
  const [name, setName] = useState('')
  const template = useMemo(() => templates.find(item => item.template_id === templateId) ?? null, [templates, templateId])

  if (!loadingTemplates && !template) return <Navigate to='/templates' replace />

  const create = async () => {
    if (!template || name.trim().length < 5) return
    setProcessing(true)
    const result = await duplicateTemplate({ apiSource, apiKey, templateId, newtemplateName: name.trim() })
    setProcessing(false)
    if (result?.error || result?.ok === false) {
      enqueueSnackbar({ message: result?.error?.message ?? 'Unable to duplicate template', variant: 'error' })
      return
    }
    const newTemplateId = result.template.template_id
    setCurrentTemplateId(newTemplateId)
    await refreshTemplates()
    enqueueSnackbar({ message: 'Template duplicated', variant: 'success' })
    navigate(`/templates/${newTemplateId}/edit`, { replace: true })
  }

  return <PageWrapper title={template ? `Duplicate Template (${template.name})` : 'Duplicate Template'} HelpText={HelpText} heading='Duplicate template' selectedTemplate={template} menu={<Menu template={template} processing={processing} setProcessing={setProcessing} />}>
    <div className='p-5 max-w-2xl flex flex-col gap-5'>
      <Typography variant='h4'>Duplicate template</Typography>
      <Typography>Create a copy of <strong>{template?.name}</strong>. The new template becomes the current template and opens in the editor.</Typography>
      <TextField label='New template name' value={name} onChange={event => setName(event.target.value.slice(0, 40))} helperText='At least 5 characters; maximum 40.' autoFocus />
      <div className='flex gap-3'>
        <Button onClick={() => navigate(`/templates/${templateId}/details`)} disabled={processing}>Cancel</Button>
        <Button variant='contained' onClick={create} disabled={processing || name.trim().length < 5}>Duplicate template</Button>
      </div>
    </div>
  </PageWrapper>
}

export default TemplateDuplicator
